import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { useDebounceCallback, useDocumentTitle, useResizeObserver } from "usehooks-ts";
import { PdfViewer } from "../core/PdfViewer";
import { useAttachmentIdFromRoute, useGameIdFromRoute, usePublisherIdFromRoute } from "../games/WithGameIdFromRoute";
import {
  useDeleteContract,
  useDeleteNonGameContract,
  useDocument,
  useGame,
  useGameContract,
  useGameContracts,
  useMyDocumentsRelatingToPublisher,
  useNonGameContract,
  usePublisherAndContacts,
  useUpdateContract,
  useUpdateNonGameContract,
} from "@seabrookstudios/pitch2table";
import { enqueueSnackbar } from "notistack";
import { LastUpdated } from "../core/LastUpdated";
import { EditableListItemText } from "../core/EditableListItemText";
import { EditableDate } from "../core/EditableDate";
import Download from "@mui/icons-material/Download";
import Lock from "@mui/icons-material/Lock";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { get } from "../network";
import { ContractTypeSelect } from "../games/contracts/ContractTypeSelect";
import { ContractPublishersSelect } from "../games/contracts/ContractPublishersSelect";
import { ContractDesignersSelect } from "../games/contracts/ContractDesignersSelect";
import { useMyProfile } from "@seabrookstudios/pitch2table";
import { UserName } from "../users/UserName";
import CardActions from "@mui/material/CardActions";
import IconButton from "@mui/material/IconButton";
import Delete from "@mui/icons-material/Delete";
import { Path } from "../Path";
import { useLocation, useNavigate } from "react-router-dom";
import { isEqual } from "lodash";
import CardContent from "@mui/material/CardContent";
import { ArrowListItem } from "../core/ArrowListItem";

const LockedContract = () => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flex={1}
      height="60vh"
      sx={{ backgroundColor: "#F6F5F5" }}
      flexDirection="column"
    >
      <Lock />
      <Typography>click to view document</Typography>
    </Box>
  );
};

export const useDownloadFile = ({ url, onError }) => {
  const ref = useRef(/** @type {HTMLAnchorElement|null} */ (null));
  const [dataUrl, setFileUrl] = useState("");

  const download = async () => {
    try {
      const response = await get(url, {
        responseType: "blob",
      });
      const { data } = response;

      const dataUrl = URL.createObjectURL(new Blob([data]));
      setFileUrl(dataUrl);

      if (ref && ref.current) {
        ref.current.click();
      }

      URL.revokeObjectURL(dataUrl);
    } catch (error) {
      // TODO: Bugsnag
      console.log(error);
      onError();
    }
  };

  return { download, ref, dataUrl };
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").Url|undefined|null} props.url
 * @param {import("@seabrookstudios/pitch2table-core").Contract} props.contract
 */
const ContractPreviewBase = ({ contract, url }) => {
  const ref = useRef(null);
  const [{ width, height }, setSize] = useState({
    width: 0,
    height: 0,
  });

  const onResize = useDebounceCallback(setSize, 200);

  useResizeObserver({
    ref,
    // @ts-expect-error
    onResize,
  });

  const handleDownload = async () => {
    if (!url) {
      return;
    }

    try {
      const response = await fetch(url);

      const blob = await response.blob();

      const internalUrl = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = internalUrl;
      link.download = `${contract.name || "contract"}.pdf`;
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(internalUrl);
    } catch (error) {
      enqueueSnackbar("Download failed", { variant: "error" });
    }
  };

  return (
    <Box p={2} pt={2} ref={ref} justifyContent="center">
      <Stack direction="row" justifyContent="flex-end">
        {url && (
          <ListItemIcon>
            <ListItemButton onClick={handleDownload}>
              <Download />
            </ListItemButton>
          </ListItemIcon>
        )}
        {url && (
          <a href={url} target="_blank" rel="noreferrer">
            <ListItemIcon>
              <ListItemButton>
                <OpenInNew />
              </ListItemButton>
            </ListItemIcon>
          </a>
        )}
      </Stack>
      <PdfViewer path={url} width={width} height={height} />
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").Contract} props.contract
 */
const ContractPreview = ({ contract }) => {
  const { data: url } = useGameContract(contract.gameId, contract.id);

  return <ContractPreviewBase url={url} contract={contract} />;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 * @param {import("@seabrookstudios/pitch2table-core").Contract} props.contract
 */
const NonGameContractPreview = ({ publisherId, contract }) => {
  const { data: url } = useNonGameContract(publisherId, contract.id);

  return <ContractPreviewBase url={url} contract={contract} />;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").Contract|import("@seabrookstudios/pitch2table-core").NonGameContract|undefined} props.contract
 * @param {import("@tanstack/react-query").UseMutationResult<import("@seabrookstudios/pitch2table-core").EditableContract>} props.updateContract
 * @param {import("@tanstack/react-query").UseMutationResult<void, Error, void, unknown>} props.deleteContract
 * @param {() => React.JSX.Element} props.UnlockedView
 * @param {React.ReactNode} props.children
 */
const BaseContactFromRoute = ({ contract, updateContract, UnlockedView, children, deleteContract }) => {
  const [locked, setLocked] = useState(true);

  const unlock = () => {
    setLocked(false);
  };

  /**
   * @param {number} signedDate
   */
  const onUpdateDate = async (signedDate) => {
    await updateContract.mutateAsync({ signedDate });
  };

  const onDeleteClick = async () => {
    await deleteContract.mutateAsync();
  };

  /**
   * @param {string} name
   */
  const onUpdateName = async (name) => {
    await updateContract.mutateAsync({ name });
  };

  useDocumentTitle(contract ? contract.name || "Unnamed Contract" : "Pitch2Table");

  if (!contract) {
    return <EmptyDetailCard text="Select a contract from the list on the left." />;
  }

  return (
    <Card elevation={1} square={false}>
      <CardContent>
        <List>
          <ListItem>
            <EditableListItemText
              primary="Document Name"
              secondary={contract.name}
              onSave={onUpdateName}
              isBusy={updateContract.isPending}
              helperText="Your reference for this contract e.g. digital rights, NDA"
            />
          </ListItem>
          <ListItem>
            <EditableDate
              title={contract.type === "receipt" ? "Statement date" : "Signed date"}
              timestamp={contract.signedDate}
              onSave={onUpdateDate}
              isBusy={updateContract.isPending}
            />
          </ListItem>
          {children}
        </List>
        <Box onClick={unlock} p={2}>
          {locked ? <LockedContract /> : <UnlockedView />}
        </Box>
      </CardContent>
      <CardActions style={{ justifyContent: "flex-end" }}>
        <IconButton onClick={onDeleteClick}>
          <Delete />
        </IconButton>
      </CardActions>
      <LastUpdated timestamp={contract.updated} />
    </Card>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").AttachmentId} props.attachmentId
 */
export const GameContract = ({ gameId, attachmentId }) => {
  const { data: game } = useGame(gameId);
  const { data: contracts = [], isLoading } = useGameContracts(gameId);
  const contract = contracts.find((c) => c.id === attachmentId);
  const navigate = useNavigate();
  const location = useLocation();

  const onContractUpdateSuccess = () => {
    enqueueSnackbar("Updated", { variant: "success" });
  };

  const redirectPostDelete = () => {
    if (location.pathname.includes("documents")) {
      navigate(Path.documents());
    } else {
      navigate(Path.game(gameId).link());
    }
  };

  const onContractDeleteSuccess = () => {
    enqueueSnackbar("Contract moved to recycling bin", { variant: "success" });

    redirectPostDelete();
  };

  const updateContract = useUpdateContract(gameId, attachmentId, onContractUpdateSuccess);
  const deleteContract = useDeleteContract(gameId, attachmentId, onContractDeleteSuccess);

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherId[]} publisherIds
   */
  const onUpdatePublisherIds = async (publisherIds) => {
    if (!contract) {
      return;
    }
    if (isEqual(contract.publisherIds, publisherIds)) {
      return;
    }

    await updateContract.mutateAsync({
      publisherIds,
      designerIds: undefined,
      signedDate: undefined,
      name: undefined,
      type: undefined,
      deleted: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/types").UserId[]} designerIds
   */
  const onUpdateDesignerIds = async (designerIds) => {
    if (!contract) {
      return;
    }
    if (isEqual(contract.designerIds, designerIds)) {
      return;
    }

    await updateContract.mutateAsync({
      designerIds,
      publisherIds: undefined,
      signedDate: undefined,
      name: undefined,
      type: undefined,
      deleted: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").ContractType} type
   */
  const onUpdateType = async (type) => {
    if (!contract) {
      return;
    }
    if (contract.type === type) {
      return;
    }

    await updateContract.mutateAsync({
      type,
      publisherIds: undefined,
      designerIds: undefined,
      signedDate: undefined,
      name: undefined,
      deleted: undefined,
    });
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!contract) {
    return <EmptyDetailCard text="Select a contract from the list on the left." />;
  }

  return (
    <BaseContactFromRoute
      contract={contract}
      updateContract={updateContract}
      deleteContract={deleteContract}
      UnlockedView={() => <ContractPreview contract={contract} />}
    >
      {game && <ArrowListItem primary="Game" secondary={game.name} to={Path.game(gameId).link()} />}
      <ListItem>
        <ContractTypeSelect value={contract.type} onChange={onUpdateType} />
      </ListItem>
      {(contract.type === "designer-publisher" || contract.type === "nda" || contract.type === "receipt") && (
        <ListItem>
          <ContractPublishersSelect gameId={gameId} value={contract.publisherIds} onChange={onUpdatePublisherIds} />
        </ListItem>
      )}
      {contract.type === "designer-publisher" && (
        <ListItem>
          <ContractDesignersSelect gameId={gameId} value={contract.designerIds} onChange={onUpdateDesignerIds} />
        </ListItem>
      )}
    </BaseContactFromRoute>
  );
};

export const ContractFromRoute = () => {
  const gameId = useGameIdFromRoute();
  const attachmentId = useAttachmentIdFromRoute();

  return <GameContract gameId={gameId} attachmentId={attachmentId} />;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 * @param {import("@seabrookstudios/pitch2table-core").AttachmentId} props.attachmentId
 */
export const NonGameContract = ({ publisherId, attachmentId }) => {
  const { data: documents = [] } = useMyDocumentsRelatingToPublisher(publisherId);
  const { data: publisherAndContact = [] } = usePublisherAndContacts(publisherId);
  const { data: profile } = useMyProfile();
  const contract = documents.find((c) => c.id === attachmentId);
  const navigate = useNavigate();
  const location = useLocation();

  const onContractUpdateSuccess = () => {
    enqueueSnackbar("Updated", { variant: "success" });
  };

  const redirectPostDelete = () => {
    if (location.pathname.includes("documents") && !location.pathname.includes("publishers")) {
      navigate(Path.documents());
    } else {
      navigate(Path.publisher(publisherId).documents());
    }
  };

  const onContractDeleteSuccess = () => {
    enqueueSnackbar("Contract moved to recycling bin", { variant: "success" });

    redirectPostDelete();
  };

  const [publisher] = publisherAndContact;
  const updateContract = useUpdateNonGameContract(publisherId, attachmentId, onContractUpdateSuccess);
  const deleteContract = useDeleteNonGameContract(publisherId, attachmentId, onContractDeleteSuccess);

  return (
    <BaseContactFromRoute
      contract={contract}
      updateContract={updateContract}
      deleteContract={deleteContract}
      UnlockedView={() => <NonGameContractPreview publisherId={publisherId} contract={contract} />}
    >
      {/* <ListItem>
        <ContractTypeSelect disabled={true} value={document.type} />
      </ListItem> */}
      <ListItem>
        <ListItemText primary="Contract Publisher" secondary={publisher ? publisher.name : <Skeleton />} />
      </ListItem>
      <ListItem>
        <ListItemText primary="Contract Designer" secondary={<UserName userId={profile ? profile.id : undefined} />} />
      </ListItem>
    </BaseContactFromRoute>
  );
};

export const NonGameContractFromRoute = () => {
  const publisherId = usePublisherIdFromRoute();
  const attachmentId = useAttachmentIdFromRoute();

  return <NonGameContract publisherId={publisherId} attachmentId={attachmentId} />;
};

export const DocumentFromRoute = () => {
  const attachmentId = useAttachmentIdFromRoute();

  const { data: document } = useDocument(attachmentId);

  if (!document) {
    return <CircularProgress />;
  }

  if (document.refType === "PublisherId") {
    return (
      <NonGameContract
        publisherId={/** @type {import("@seabrookstudios/pitch2table-core").PublisherId} */ (document.refId)}
        attachmentId={document.id}
      />
    );
  }

  return <GameContract gameId={document.refId} attachmentId={document.id} />;
};
